/**
 * Header Theme Blocks placement.
 *
 * Theme block settings cannot be read from outside the block itself, so the
 * header section renders every theme block into a hidden source region:
 *   #HeaderThemeBlocksSource [data-header-blocks-source]
 *
 * Each block self-tags with a `data-header-block` attribute (and, for
 * nav mega menus, a `data-menu-title-handle`). This script moves/clones those
 * rendered blocks into their final positions in the header.
 *
 *   - store-switcher → moved into [data-header-block-target="store-switcher"]
 *   - nav-mega      → cloned into the desktop nav <li> whose
 *                      `data-link-title-handle` matches, and into the mobile
 *                      drawer item's nav-mega slot.
 *
 * Important: customized built-in elements (`<details is="details-mega">`,
 * `<details is="menu-drawer-details">`) must be created via the HTML parser
 * (innerHTML / template) so their constructors run AFTER children are present.
 * Their constructors immediately read `firstElementChild` / `lastElementChild`,
 * so building them programmatically (createElement + appendChild) leaves the
 * element broken.
 */
(function () {
  'use strict';

  const SOURCE_SELECTOR = '[data-header-blocks-source]';

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  function escapeAttr(str) {
    return escapeHtml(str).replace(/"/g, '&quot;');
  }

  function cssEscape(value) {
    if (window.CSS && CSS.escape) return CSS.escape(value);
    return String(value).replace(/[^a-zA-Z0-9_-]/g, function (ch) {
      return '\\' + ch;
    });
  }

  // Same chevron as the top-level dropdown summary in snippets/desktop-menu.liquid.
  const DESKTOP_CARET_SVG =
    '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
      '<path d="M9.75 4.5L6 8.25L2.25 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  // Same arrow as snippets/icon-caret.liquid, used in the mobile drawer.
  function drawerCaretSvg(size) {
    return (
      '<svg class="icon icon-arrow icon--' + size + '" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
        '<path d="M7.5 3.74121L13.75 9.99121L7.5 16.2412" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>'
    );
  }

  function placeStoreSwitcher(sourceRoot) {
    const source =
      sourceRoot.querySelector('[data-header-block="store-switcher"]') ||
      document.querySelector('[data-header-block="store-switcher"]');
    const mobileTarget = document.querySelector('[data-header-block-target="store-switcher"]');
    const desktopTarget = document.querySelector('[data-header-block-target="store-switcher-desktop"]');
    const desktop = window.matchMedia('(min-width: 1024px)').matches;
    const target = desktop ? desktopTarget : mobileTarget;
    if (source && target && !target.contains(source)) {
      target.replaceChildren(source);
    }

    // On smaller screens the row sits above the sticky header and scrolls away.
    const section = document.querySelector('.header-section');
    if (!desktop && mobileTarget && section && section.parentElement && mobileTarget.parentElement !== section.parentElement) {
      section.parentElement.insertBefore(mobileTarget, section);
    }

    // The store-switcher block also emits a snackbar and/or a store-switch
    // overlay as siblings of the pill wrapper. They are `position: fixed`, so
    // we relocate them to <body> — otherwise they stay trapped inside the
    // hidden source container and never render.
    sourceRoot
      .querySelectorAll('.store-switcher-snackbar, .store-switcher-overlay')
      .forEach((el) => {
        // Avoid duplicates if placeAll() runs again (theme editor).
        const existing = el.id && document.getElementById(el.id);
        if (existing && existing !== el) existing.remove();
        document.body.appendChild(el);
      });

    if (typeof window.__initStoreSwitcherLinks === 'function') {
      window.__initStoreSwitcherLinks(document);
    }
  }

  function buildDesktopMegaHTML(blockId, linkUrl, linkTitle, trigger, innerHTML, panelWidth) {
    const panelClass = panelWidth === 'boxed' || panelWidth === 'nav' ? ' nav-mega--boxed' : '';
    return (
      '<details ' +
      (blockId ? 'id="Mega-menu-item-' + escapeAttr(blockId) + '" ' : '') +
      'is="details-mega" ' +
      'trigger="' + escapeAttr(trigger) + '" ' +
      'level="top">' +
        '<summary data-link="' + escapeAttr(linkUrl) + '" class="z-2 font-navigation">' +
          '<span class="menu__item flex items-center relative">' +
            '<span class="reversed-link flex items-center">' +
              escapeHtml(linkTitle) +
              DESKTOP_CARET_SVG +
            '</span>' +
          '</span>' +
        '</summary>' +
        '<div class="nav-mega v-scrollable' + panelClass + '">' +
          '<div class="nav-mega__container">' +
            '<div class="nav-mega__wrapper">' +
              innerHTML +
            '</div>' +
          '</div>' +
        '</div>' +
      '</details>'
    );
  }

  function groupMegaMenuColumns(root) {
    if (!root) return;
    root.querySelectorAll('.nav-mega__columns').forEach((cols) => {
      if (cols.querySelector(':scope > .nav-mega__nav-box, :scope > .nav-mega__promo-box')) return;
      const navs = [];
      const promos = [];
      Array.from(cols.children).forEach((el) => {
        if (el.classList.contains('nav-mega__column')) navs.push(el);
        else if (el.classList.contains('nav-mega__promo-col')) promos.push(el);
      });
      if (!navs.length && !promos.length) return;

      const navBox = document.createElement('div');
      navBox.className = 'nav-mega__nav-box';
      const promoBox = document.createElement('div');
      promoBox.className = 'nav-mega__promo-box';
      navs.forEach((el) => navBox.appendChild(el));
      promos.forEach((el) => promoBox.appendChild(el));
      const inner = cols.closest('.nav-mega__inner');
      const headline = inner && inner.querySelector(':scope > .nav-mega__headline');
      if (headline && promos.length) promoBox.prepend(headline);
      cols.replaceChildren();
      if (navs.length) cols.appendChild(navBox);
      if (promos.length) cols.appendChild(promoBox);
    });
  }

  function placeDesktopMegaMenus(sourceRoot) {
    groupMegaMenuColumns(sourceRoot);
    const sources = sourceRoot.querySelectorAll('[data-header-block="nav-mega"]');
    sources.forEach((source) => {
      const handle = source.dataset.menuTitleHandle;
      if (!handle) return;
      const matches = document.querySelectorAll(
        '[data-nav-link][data-link-title-handle="' + cssEscape(handle) + '"]'
      );
      if (!matches.length) return;
      const innerSource = source.querySelector('.nav-mega__inner');
      if (!innerSource) return;
      const innerHTML = innerSource.outerHTML;
      matches.forEach((li) => {
        const navRoot = li.closest('[data-nav-mega-root]');
        const trigger = (navRoot && navRoot.dataset.menuTrigger) || 'hover';
        // Setting innerHTML triggers the HTML parser, which constructs all
        // children first, then upgrades the customized built-in <details>.
        li.innerHTML = buildDesktopMegaHTML(
          source.dataset.blockId,
          li.dataset.linkUrl,
          li.dataset.linkTitle,
          trigger,
          innerHTML,
          source.dataset.panelWidth
        );
      });
    });
  }

  function buildMobileDetailsHTML(linkTitle, megaInnerHTML) {
    return (
      '<details is="menu-drawer-details" level="top">' +
        '<summary class="menu-drawer__item-link flex items-center justify-between">' +
          '<span>' + escapeHtml(linkTitle) + '</span>' +
          drawerCaretSvg('medium') +
        '</summary>' +
        '<div class="menu-drawer__submenu absolute z-1 flex flex-col w-full h-full" data-parent>' +
          '<button type="button" class="menu-drawer__item-back-link font-navigation flex items-center gap-2 w-full">' +
            drawerCaretSvg('small') +
            escapeHtml(linkTitle) +
          '</button>' +
          '<ul class="flex flex-col h-full v-scrollable" data-parent role="list">' +
            '<li class="menu-drawer__item menu-drawer__nav-mega" data-nav-mega-slot>' +
              megaInnerHTML +
            '</li>' +
          '</ul>' +
        '</div>' +
      '</details>'
    );
  }

  function placeMobileMegaMenus(sourceRoot) {
    const sources = sourceRoot.querySelectorAll('[data-header-block="nav-mega"]');
    sources.forEach((source) => {
      const handle = source.dataset.menuTitleHandle;
      if (!handle) return;
      const matches = document.querySelectorAll(
        '[data-mobile-nav-link][data-link-title-handle="' + cssEscape(handle) + '"]'
      );
      if (!matches.length) return;
      const innerSource = source.querySelector('.nav-mega__inner');
      if (!innerSource) return;
      const innerHTML = innerSource.outerHTML;
      matches.forEach((li) => {
        const existingDetails = li.querySelector(':scope > details');
        if (existingDetails) {
          // The link already has children → append a nav-mega slot at the end
          // of the existing submenu list (preserve existing nav children).
          const submenuList = existingDetails.querySelector('ul[data-parent]');
          if (submenuList) {
            // Remove any prior nav-mega slot, then append a fresh one.
            const prior = submenuList.querySelector('[data-nav-mega-slot]');
            if (prior) prior.remove();
            const slot = document.createElement('li');
            slot.className = 'menu-drawer__item menu-drawer__nav-mega';
            slot.setAttribute('data-nav-mega-slot', '');
            slot.innerHTML = innerHTML;
            submenuList.appendChild(slot);
          }
        } else {
          // Plain link with no children → wrap the <li> in a fresh <details>.
          li.innerHTML = buildMobileDetailsHTML(li.dataset.linkTitle, innerHTML);
        }
      });
    });
  }

  function placeAll() {
    const sourceRoot = document.querySelector(SOURCE_SELECTOR);
    if (!sourceRoot) return;
    placeStoreSwitcher(sourceRoot);
    placeDesktopMegaMenus(sourceRoot);
    placeMobileMegaMenus(sourceRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', placeAll, { once: true });
  } else {
    placeAll();
  }

  window.matchMedia('(min-width: 1024px)').addEventListener('change', function () {
    const sourceRoot = document.querySelector(SOURCE_SELECTOR);
    if (sourceRoot) placeStoreSwitcher(sourceRoot);
  });

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      if (e.target && e.target.querySelector && e.target.querySelector(SOURCE_SELECTOR)) {
        placeAll();
      }
    });
    document.addEventListener('shopify:block:select', () => placeAll());
  }
})();
